/**
 * AntispamSettingsPage — 反垃圾设置（独立页）
 * 灰名单开关 / 重试延迟、DNSBL 开关 / 黑名单区域。
 * 数据存 server_config 表，后端 SMTP 入站检查时实时读取，保存即生效，无需重启服务。
 */
import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  makeStyles,
  tokens,
  Text,
  Card,
  Field,
  InfoLabel,
  Input,
  Textarea,
  Switch,
  Button,
  Spinner,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Divider,
} from '@fluentui/react-components'
import { SaveRegular } from '@fluentui/react-icons'
import { antispamApi } from '@/api/antispam'
import { toast } from '@/lib/toast'
import { ApiError } from '@/api/client'

const useStyles = makeStyles({
  root: { display: 'flex', flexDirection: 'column', gap: '16px', width: '100%', minWidth: 0 },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '8px',
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    paddingTop: '16px',
  },
  hint: { fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3 },
})

export default function AntispamSettingsPage() {
  const styles = useStyles()
  const queryClient = useQueryClient()
  const { data, isLoading } = useQuery({ queryKey: ['antispam'], queryFn: () => antispamApi.get() })

  const [greylistEnabled, setGreylistEnabled] = useState(true)
  const [greylistDelay, setGreylistDelay] = useState(300)
  const [dnsblEnabled, setDnsblEnabled] = useState(true)
  const [dnsblZones, setDnsblZones] = useState('')
  const [minInterval, setMinInterval] = useState(15)

  useEffect(() => {
    if (data) {
      setGreylistEnabled(data.greylist_enabled)
      setGreylistDelay(data.greylist_delay_secs)
      setDnsblEnabled(data.dnsbl_enabled)
      setDnsblZones((data.dnsbl_zones ?? []).join('\n'))
      setMinInterval(data.min_receive_interval_secs ?? 15)
    }
  }, [data])

  const saveMutation = useMutation({
    mutationFn: () =>
      antispamApi.put({
        greylist_enabled: greylistEnabled,
        greylist_delay_secs: greylistDelay,
        dnsbl_enabled: dnsblEnabled,
        dnsbl_zones: dnsblZones.split('\n').map((s) => s.trim()).filter(Boolean),
        min_receive_interval_secs: minInterval,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['antispam'] })
      toast.success('已保存', '反垃圾设置已更新，入站邮件实时生效')
    },
    onError: (err) => toast.error('保存失败', err instanceof ApiError ? err.message : '请检查输入'),
  })

  const dirty =
    !!data &&
    (data.greylist_enabled !== greylistEnabled ||
      data.greylist_delay_secs !== greylistDelay ||
      data.dnsbl_enabled !== dnsblEnabled ||
      (data.dnsbl_zones ?? []).join('\n') !== dnsblZones.trim() ||
      (data.min_receive_interval_secs ?? 15) !== minInterval)

  if (isLoading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <Spinner size="large" label="加载中..." />
      </div>
    )
  }

  const reset = () => {
    if (!data) return
    setGreylistEnabled(data.greylist_enabled)
    setGreylistDelay(data.greylist_delay_secs)
    setDnsblEnabled(data.dnsbl_enabled)
    setDnsblZones((data.dnsbl_zones ?? []).join('\n'))
    setMinInterval(data.min_receive_interval_secs ?? 15)
  }

  return (
    <div className={styles.root}>
      <MessageBar intent="info">
        <MessageBarBody>
          <MessageBarTitle>实时生效</MessageBarTitle>
          以下设置保存在服务器配置中，SMTP 入站检查时实时读取，保存后立即对新连接生效，无需重启服务。
        </MessageBarBody>
      </MessageBar>

      <Card size="small">
        <Text style={{ fontWeight: 600 }}>灰名单（Greylisting）</Text>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginTop: 12 }}>
          <Field
            label={<InfoLabel info="首次见到陌生「发件 IP + 发件人 + 收件人」组合时返回 4xx 临时拒；正规邮件服务器会自动重试并放行，垃圾僵尸通常一击即弃。">启用灰名单</InfoLabel>}
          >
            <Switch
              checked={greylistEnabled}
              onChange={(_, d) => setGreylistEnabled(d.checked)}
              label={greylistEnabled ? '已启用' : '已关闭'}
            />
          </Field>
          <Field
            label={<InfoLabel info="首次连接被临时拒后，发件方需等待多久再重试（秒）。">重试延迟（秒）</InfoLabel>}
            hint="默认 300 秒（5 分钟）"
          >
            <Input
              type="number"
              value={String(greylistDelay)}
              onChange={(_, d) => setGreylistDelay(Number(d.value))}
              style={{ maxWidth: '200px' }}
              disabled={!greylistEnabled}
            />
          </Field>
          <Text className={styles.hint}>
            代价：合法邮件会有几分钟延迟；关闭可让收信即时，但反垃圾能力减弱。
          </Text>
        </div>
      </Card>

      <Card size="small">
        <Text style={{ fontWeight: 600 }}>入站最小接收间隔</Text>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginTop: 12 }}>
          <Field
            label={<InfoLabel info="同一「来源 IP + 发件人 + 收件人」组合在间隔内只允许成功接收一封邮件，其余直接拒收。用于防止同一来源高频投递进行爆破或刷爆收件箱。">
              最小接收间隔（秒）
            </InfoLabel>}
            hint="默认 15 秒；设为 0 表示不限速"
          >
            <Input
              type="number"
              value={String(minInterval)}
              onChange={(_, d) => setMinInterval(Number(d.value))}
              style={{ maxWidth: '200px' }}
            />
          </Field>
          <Text className={styles.hint}>
            例如设为 15：同一来源在 15 秒内连发多封，仅第一封入箱，其余被临时拒收（4xx）。
          </Text>
        </div>
      </Card>

      <Divider />

      <Card size="small">
        <Text style={{ fontWeight: 600 }}>DNSBL 黑名单</Text>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginTop: 12 }}>
          <Field label={<InfoLabel info="连接阶段查询 DNS 黑名单，命中则直接拒收。">启用 DNSBL</InfoLabel>}>
            <Switch
              checked={dnsblEnabled}
              onChange={(_, d) => setDnsblEnabled(d.checked)}
              label={dnsblEnabled ? '已启用' : '已关闭'}
            />
          </Field>
          <Field
            label={<InfoLabel info="每行一个黑名单区域。注意：勿添加 zen.spamhaus.org——其 PBL 会把腾讯云/阿里云等数据中心 IP 整体误杀，导致入站被硬拒。">黑名单区域（Zones）</InfoLabel>}
            hint="每行一个，如 bl.spamcop.net"
          >
            <Textarea
              value={dnsblZones}
              onChange={(_, d) => setDnsblZones(d.value)}
              placeholder={'bl.spamcop.net'}
              disabled={!dnsblEnabled}
              style={{ minHeight: 96 }}
            />
          </Field>
        </div>
      </Card>

      <div className={styles.actions}>
        {dirty && (
          <Button appearance="subtle" onClick={reset}>
            撤销
          </Button>
        )}
        <Button
          appearance="primary"
          icon={<SaveRegular />}
          onClick={() => saveMutation.mutate()}
          disabled={!dirty || saveMutation.isPending}
        >
          {saveMutation.isPending ? '保存中…' : '保存'}
        </Button>
      </div>
    </div>
  )
}
